import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { FiUpload, FiTrash2, FiFileText, FiPlus } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const ManageResources = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ file: null, title: '', description: '' });
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/manage_resources.php`);
            const data = await response.json();
            if (data.status === 'success') {
                setResources(data.data);
            }
        } catch (error) {
            console.error("Error fetching resources:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!formData.file || !formData.title) return;

        setUploading(true);
        const data = new FormData();
        data.append('file', formData.file);
        data.append('title', formData.title);
        data.append('description', formData.description);

        try {
            const response = await fetch(`${API_BASE_URL}/manage_resources.php`, {
                method: 'POST',
                body: data
            });
            const result = await response.json();
            if (result.status === 'success') {
                alert("Resource uploaded successfully!");
                setShowModal(false);
                setFormData({ file: null, title: '', description: '' });
                fetchResources();
            } else {
                alert(result.message);
            }
        } catch (error) {
            alert("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this resource?")) return;

        try {
            const response = await fetch(`${API_BASE_URL}/manage_resources.php?id=${id}`, {
                method: 'DELETE'
            });
            const result = await response.json();
            if (result.status === 'success') {
                setResources(resources.filter(res => res.id !== id));
            }
        } catch (error) {
            alert("Delete failed");
        }
    };

    return (
        <DashboardLayout title="Manage Resources">
            <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-3xl font-bold text-slate-900">Learning Resources</h2>
                        <p className="text-slate-500">Manage PDF study materials for students.</p>
                    </div>
                    <button 
                        onClick={() => setShowModal(true)}
                        className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
                    >
                        <FiPlus /> Add Resource
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-6 py-4 font-bold text-slate-900">Title</th>
                                    <th className="px-6 py-4 font-bold text-slate-900">Description</th>
                                    <th className="px-6 py-4 font-bold text-slate-900">Uploaded</th>
                                    <th className="px-6 py-4 font-bold text-slate-900">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {resources.map(res => (
                                    <tr key={res.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-slate-900">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                                    <FiFileText />
                                                </div>
                                                {res.title}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-500 max-w-xs truncate">{res.description}</td>
                                        <td className="px-6 py-4 text-slate-500">{new Date(res.created_at).toLocaleDateString()}</td>
                                        <td className="px-6 py-4">
                                            <button 
                                                onClick={() => handleDelete(res.id)}
                                                className="text-red-500 hover:text-red-700 transition-colors"
                                            >
                                                <FiTrash2 size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Upload Modal */}
                <AnimatePresence>
                    {showModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                            >
                                <div className="bg-blue-600 p-6 text-white">
                                    <h3 className="text-xl font-bold">Add New Resource</h3>
                                    <p className="text-blue-100 text-sm">Upload a PDF file and provide details.</p>
                                </div>
                                <form onSubmit={handleUpload} className="p-8">
                                    <div className="mb-6">
                                        <label className="block text-slate-700 font-semibold mb-2">Resource Title</label>
                                        <input 
                                            type="text" 
                                            className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            placeholder="e.g., Mathematics Lecture Notes"
                                            required
                                        />
                                    </div>
                                    <div className="mb-6">
                                        <label className="block text-slate-700 font-semibold mb-2">Description</label>
                                        <textarea 
                                            className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none h-24 resize-none"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Short summary of the resource..."
                                        />
                                    </div>
                                    <div className="mb-8">
                                        <label className="block text-slate-700 font-semibold mb-2">Select PDF File</label>
                                        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center hover:border-blue-400 transition-colors relative">
                                            <input 
                                                type="file" 
                                                accept=".pdf"
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                                                required
                                            />
                                            {formData.file ? (
                                                <div className="text-blue-600 font-medium">
                                                    <FiFileText className="mx-auto text-3xl mb-2" />
                                                    {formData.file.name}
                                                </div>
                                            ) : (
                                                <div className="text-slate-400">
                                                    <FiUpload className="mx-auto text-3xl mb-2" />
                                                    <p>Click to upload PDF</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <button 
                                            type="button"
                                            onClick={() => setShowModal(false)}
                                            className="flex-1 px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="submit"
                                            disabled={uploading}
                                            className="flex-1 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                                        >
                                            {uploading ? 'Uploading...' : 'Add Resource'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout>
    );
};

export default ManageResources;
